import React from 'react';
import { 
    Button, 
    Stack, 
    Box, 
    IconButton, 
    useMediaQuery, 
    useTheme, 
    SvgIconTypeMap,
    Tooltip // Добавляем Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { useTranslation } from 'react-i18next';

type MuiIcon = OverridableComponent<SvgIconTypeMap<{}, "svg">> & { muiName: string };

export interface ActionButtonProp {
  type: 'edit' | 'delete' | 'extended';
  action: () => void;
  icon?: MuiIcon;
  tooltip?: string; // Добавляем опциональное поле для кастомного текста подсказки
}

interface ActionButtonsProps {
  actions: ActionButtonProp[];
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ actions }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  // Оставляем логику как в оригинале (всегда false по вашему решению)
  const isWideScreen = false && useMediaQuery(theme.breakpoints.up('sm')); 

  const actionConfig: Record<string, { icon: MuiIcon, color: 'primary' | 'error' | 'info', label: string }> = {
    edit: { icon: EditIcon, color: 'primary', label: t('common.Edit') },
    delete: { icon: DeleteIcon, color: 'error', label: t('common.Delete') },
    extended: { icon: MoreHorizIcon, color: 'info', label: t('common.Details') },
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Stack direction="row" spacing={1}>
        {actions.map((button, index) => {
          const config = actionConfig[button.type];
          if (!config) return null;

          const IconComponent = button.icon || config.icon;
          // Используем переданный tooltip или стандартный label из конфига
          const tooltipTitle = button.tooltip || config.label;

          const renderButton = () => {
            if (isWideScreen) {
              return (
                <Button
                  onClick={button.action}
                  size="small"
                  color={config.color}
                  variant='outlined'
                  startIcon={<IconComponent fontSize="small" />}
                >
                  {config.label}
                </Button>
              );
            } else {
              return (
                <IconButton
                  onClick={button.action}
                  size="small"
                  color={config.color}
                  aria-label={config.label}
                >
                  <IconComponent fontSize="small" />
                </IconButton>
              );
            }
          };

          return (
            <Tooltip key={index} title={tooltipTitle} arrow>
              {/* Оборачиваем в span, если кнопка может быть disabled (Tooltip требует focus/hover на валидном DOM узле) */}
              <span>
                {renderButton()}
              </span>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ActionButtons;